import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  Plus,
  Trash2,
  ChevronRight,
  ToggleLeft,
  ToggleRight,
  LayoutTemplate,
  Settings2,
  X,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input, Textarea } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  fieldsJson: FieldDef[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  listingTemplateId: string | null;
  detailTemplateId: string | null;
}

const FIELD_TYPES = ['text', 'rich_text', 'number', 'boolean', 'date', 'datetime', 'image', 'select', 'color'] as const;
type FieldType = typeof FIELD_TYPES[number];

interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: string[];
}

const EMPTY_FIELD: FieldDef = { key: '', label: '', type: 'text' };

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsCollectionsPage() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [editTarget, setEditTarget] = useState<Collection | null>(null);
  const [editListingTmpl, setEditListingTmpl] = useState<string>('');
  const [editDetailTmpl, setEditDetailTmpl] = useState<string>('');

  // Schema editor state
  const [schemaTarget, setSchemaTarget] = useState<Collection | null>(null);
  const [schemaFields, setSchemaFields] = useState<FieldDef[]>([]);
  const [newField, setNewField] = useState<FieldDef>(EMPTY_FIELD);
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [showAddField, setShowAddField] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);
  const [editingFieldOptions, setEditingFieldOptions] = useState('');

  const { data = [], isLoading } = useQuery<Collection[]>({
    queryKey: ['cms-collections', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
  });

  const { data: templates = [] } = useQuery<Array<{ id: string; name: string; scope: string }>>({
    queryKey: ['collection-templates', spaceId],
    queryFn: () =>
      api.get(`/api/v1/cms/spaces/${spaceId}/collection-templates`).then((r) => r.data),
    enabled: !!spaceId,
  });
  const listingTemplates = templates.filter((t) => t.scope === 'listing');
  const detailTemplates = templates.filter((t) => t.scope === 'detail');

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post(`/api/v1/cms/spaces/${spaceId}/collections`, body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['cms-collections', spaceId] });
      setShowCreate(false);
      setName('');
      setSlug('');
      setDescription('');
      toast.success('Collection created');
      navigate(`/spaces/${spaceId}/collections/${(res.data as Collection).id}/entries`);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create collection')),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/api/v1/cms/spaces/${spaceId}/collections/${id}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cms-collections', spaceId] }),
    onError: (err) => toast.error(extractError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/cms/spaces/${spaceId}/collections/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-collections', spaceId] });
      setDeleteTarget(null);
      toast.success('Collection deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateSchemaMutation = useMutation({
    mutationFn: ({ id, fieldsJson }: { id: string; fieldsJson: FieldDef[] }) =>
      api.patch(`/api/v1/cms/spaces/${spaceId}/collections/${id}`, { fieldsJson }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cms-collections', spaceId] });
      setSchemaTarget(null);
      toast.success('Schema saved');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, listingTemplateId, detailTemplateId }: { id: string; listingTemplateId: string | null; detailTemplateId: string | null }) =>
      api.patch(`/api/v1/cms/spaces/${spaceId}/collections/${id}`, { listingTemplateId, detailTemplateId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cms-collections', spaceId] });
      setEditTarget(null);
      toast.success('Template updated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function handleCreate() {
    if (!name.trim() || !slug.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      slug: slug.trim(),
      description: description.trim() || undefined,
    });
  }

  return (
    <div>
      <PageHeader
        title="Collections"
        description="Structured content types with custom fields and entries"
        action={
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Collection
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={5} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<Database size={26} />}
          title="No collections yet"
          description="Create a collection to define structured content types with custom fields"
          action={{ label: 'New Collection', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Fields
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((col) => (
                <tr
                  key={col.id}
                  className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/spaces/${spaceId}/collections/${col.id}/entries`)}
                >
                  <td className="px-4 py-3 font-medium text-zinc-900">
                    {col.name}
                    {col.description && (
                      <div className="text-xs text-zinc-400 mt-0.5 truncate max-w-[220px]">
                        {col.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{col.slug}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">
                    {Array.isArray(col.fieldsJson) ? col.fieldsJson.length : 0} fields
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMutation.mutate({ id: col.id, isActive: !col.isActive });
                      }}
                      disabled={toggleMutation.isPending}
                      className="flex items-center gap-1.5 text-xs"
                      title={col.isActive ? 'Click to deactivate' : 'Click to activate'}
                    >
                      {col.isActive ? (
                        <>
                          <ToggleRight size={16} className="text-emerald-500" />
                          <span className="text-emerald-600">Active</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft size={16} className="text-zinc-400" />
                          <span className="text-zinc-400">Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmt(col.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSchemaTarget(col);
                          setSchemaFields(Array.isArray(col.fieldsJson) ? col.fieldsJson : []);
                          setNewField(EMPTY_FIELD);
                          setNewFieldOptions('');
                          setShowAddField(false);
                        }}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                        title="Edit schema"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditTarget(col);
                          setEditListingTmpl(col.listingTemplateId ?? '');
                          setEditDetailTmpl(col.detailTemplateId ?? '');
                        }}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
                        title="Set template"
                      >
                        <LayoutTemplate className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(col);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setName('');
          setSlug('');
          setDescription('');
        }}
        title="New Collection"
      >
        <div className="space-y-4">
          <Input
            label="Name *"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSlug(slugify(e.target.value));
            }}
            placeholder="Blog Posts"
          />
          <div>
            <Input
              label="Slug *"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="blog-posts"
              className="font-mono text-xs"
            />
            <p className="text-xs text-zinc-400 mt-1">Used in API and headless delivery</p>
          </div>
          <Textarea
            label="Description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </div>
        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            loading={createMutation.isPending}
            disabled={!name.trim() || !slug.trim()}
            onClick={handleCreate}
          >
            Create Collection
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete collection?"
        message={`"${deleteTarget?.name}" and all its entries will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />

      {schemaTarget && (
        <Modal
          open={!!schemaTarget}
          onClose={() => setSchemaTarget(null)}
          title={`Schema: ${schemaTarget.name}`}
        >
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {schemaFields.length === 0 && !showAddField && (
              <p className="text-sm text-zinc-400 py-4 text-center">No fields defined yet.</p>
            )}
            {schemaFields.map((f, i) => (
              <div key={i} className="border border-zinc-200 rounded-lg overflow-hidden">
                {/* collapsed row — click to expand */}
                <div
                  className="flex items-center gap-2 px-3 py-2 bg-zinc-50 cursor-pointer hover:bg-zinc-100 transition-colors"
                  onClick={() => {
                    if (editingFieldIndex === i) { setEditingFieldIndex(null); return; }
                    setEditingFieldIndex(i);
                    setEditingFieldOptions(f.options?.join(', ') ?? '');
                    setShowAddField(false);
                  }}
                >
                  <span className="font-mono text-xs text-zinc-500 w-28 truncate">{f.key}</span>
                  <span className="text-sm text-zinc-800 flex-1 truncate">{f.label}</span>
                  <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full">{f.type}</span>
                  {f.required && <span className="text-xs text-red-400">req</span>}
                  <button
                    onClick={(e) => { e.stopPropagation(); setSchemaFields((prev) => prev.filter((_, j) => j !== i)); if (editingFieldIndex === i) setEditingFieldIndex(null); }}
                    className="p-1 text-zinc-300 hover:text-red-400 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* expanded edit form */}
                {editingFieldIndex === i && (
                  <div className="px-3 py-3 bg-white border-t border-zinc-100 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-zinc-600 mb-1">Key</label>
                        <input
                          className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-400"
                          value={f.key}
                          onChange={(e) => setSchemaFields((prev) => prev.map((x, j) => j === i ? { ...x, key: e.target.value.replace(/\s+/g, '_').toLowerCase() } : x))}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-zinc-600 mb-1">Label</label>
                        <input
                          className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
                          value={f.label}
                          onChange={(e) => setSchemaFields((prev) => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-zinc-600 mb-1">Type</label>
                        <select
                          className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
                          value={f.type}
                          onChange={(e) => setSchemaFields((prev) => prev.map((x, j) => j === i ? { ...x, type: e.target.value as FieldType } : x))}
                        >
                          {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="flex items-end pb-0.5">
                        <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!f.required}
                            onChange={(e) => setSchemaFields((prev) => prev.map((x, j) => j === i ? { ...x, required: e.target.checked } : x))}
                            className="rounded"
                          />
                          Required
                        </label>
                      </div>
                    </div>
                    {f.type === 'select' && (
                      <div>
                        <label className="block text-xs font-medium text-zinc-600 mb-1">Options (comma-separated)</label>
                        <input
                          className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
                          value={editingFieldOptions}
                          onChange={(e) => {
                            setEditingFieldOptions(e.target.value);
                            setSchemaFields((prev) => prev.map((x, j) => j === i ? { ...x, options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) } : x));
                          }}
                        />
                      </div>
                    )}
                    <div className="flex justify-end">
                      <button onClick={() => setEditingFieldIndex(null)} className="text-xs text-violet-600 hover:text-violet-800 font-medium px-2 py-1">Done</button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {showAddField && (
              <div className="border border-violet-200 rounded-lg p-3 space-y-2 bg-violet-50/40">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Key *</label>
                    <input
                      className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-400"
                      placeholder="field_key"
                      value={newField.key}
                      onChange={(e) => setNewField((f) => ({ ...f, key: e.target.value.replace(/\s+/g, '_').toLowerCase() }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Label *</label>
                    <input
                      className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
                      placeholder="Field Label"
                      value={newField.label}
                      onChange={(e) => setNewField((f) => ({ ...f, label: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Type *</label>
                    <select
                      className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
                      value={newField.type}
                      onChange={(e) => setNewField((f) => ({ ...f, type: e.target.value as FieldType }))}
                    >
                      {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end pb-0.5">
                    <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!newField.required}
                        onChange={(e) => setNewField((f) => ({ ...f, required: e.target.checked }))}
                        className="rounded"
                      />
                      Required
                    </label>
                  </div>
                </div>
                {newField.type === 'select' && (
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 mb-1">Options (comma-separated) *</label>
                    <input
                      className="w-full border border-zinc-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-violet-400"
                      placeholder="Option A, Option B, Option C"
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                    />
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => { setShowAddField(false); setNewField(EMPTY_FIELD); setNewFieldOptions(''); }}
                    className="text-xs text-zinc-400 hover:text-zinc-600 px-2 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!newField.key.trim() || !newField.label.trim()) return;
                      if (newField.type === 'select' && !newFieldOptions.trim()) return;
                      const field: FieldDef = {
                        ...newField,
                        key: newField.key.trim(),
                        label: newField.label.trim(),
                        ...(newField.type === 'select' ? { options: newFieldOptions.split(',').map((o) => o.trim()).filter(Boolean) } : {}),
                      };
                      setSchemaFields((prev) => [...prev, field]);
                      setNewField(EMPTY_FIELD);
                      setNewFieldOptions('');
                      setShowAddField(false);
                    }}
                    className="text-xs bg-violet-600 text-white px-3 py-1 rounded hover:bg-violet-700 transition-colors"
                  >
                    Add field
                  </button>
                </div>
              </div>
            )}
          </div>

          {!showAddField && (
            <button
              onClick={() => setShowAddField(true)}
              className="mt-3 flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-800 font-medium"
            >
              <Plus className="w-3.5 h-3.5" /> Add field
            </button>
          )}

          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setSchemaTarget(null)}>Cancel</Button>
            <Button
              loading={updateSchemaMutation.isPending}
              onClick={() => schemaTarget && updateSchemaMutation.mutate({ id: schemaTarget.id, fieldsJson: schemaFields })}
            >
              Save schema
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {editTarget && (
        <Modal
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          title={`Templates for "${editTarget.name}"`}
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Listing template</label>
              <select
                value={editListingTmpl}
                onChange={(e) => setEditListingTmpl(e.target.value)}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
              >
                <option value="">Default layout</option>
                {listingTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Detail template</label>
              <select
                value={editDetailTmpl}
                onChange={(e) => setEditDetailTmpl(e.target.value)}
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
              >
                <option value="">Default layout</option>
                {detailTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              onClick={() =>
                updateTemplateMutation.mutate({
                  id: editTarget.id,
                  listingTemplateId: editListingTmpl || null,
                  detailTemplateId: editDetailTmpl || null,
                })
              }
              loading={updateTemplateMutation.isPending}
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}
